import { RRule, datetime } from '@a1/shared/rrule';
import { affiliateBookingStatuses } from '@a1/shared';

// project constants
import { bookingStatuses, preferredTimeFrameOptions } from 'constants/bookings';
import { managerLocationOptions } from 'constants/managerOptions';
import { calendarModes } from 'constants/calendar';

// project utils
import callAzureFunction from 'utils-ts/call-azure-function';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import { areIntervalsOverlapping } from './date-helpers';
import { capitalizeFirstLetter, getRRuleDateTimeComponents } from './form-helpers';

// dayjs
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Gets a `Dayjs` object with a set time depending on the provided parameter values.
 * @param {Number} hours Number of hours
 * @param {Number} min Number of minutes
 * @param {Number} sec Number of seconds
 * @param {Number} ms Number of milliseconds
 * @param {Date | dayjs.Dayjs | string} date Date to use as a base, if not provided, current Date is used
 * @returns {dayjs.Dayjs} Dayjs object
 */
const getDateWithCustomTime = (hours = 0, min = 0, sec = 0, ms = 0, date = null) => {
  const newDate = date ? dayjs(date) : dayjs();
  return newDate.hour(hours).minute(min).second(sec).millisecond(ms);
};

const validHours = (value) => Boolean(typeof value === 'number' && value >= 0 && value <= 23);

const validMinutes = (value) => Boolean(typeof value === 'number' && value >= 0 && value <= 59);

/**
 * Determines if the given day is a valid work day or not.
 * @param {Date} date Date to be checked
 * @param {object} location Location object. If `location` is not provided, `locationWorkDays` will be used next
 * @param {string | null} locationWorkDays Comma separated list of work days e.g. `1,2,4,5`
 * @param workDaysArray {Array<number>} Array of work days
 * @returns {boolean} `true` if day is valid, `false` otherwise
 * @deprecated Work days are not saved anymore since location schedules update.
 */
const dayIsValidWorkday = (date, location = null, locationWorkDays = null, workDaysArray = null) => {
  if (!(date instanceof Date || date instanceof dayjs)) return false;

  const workDays =
    location?.workDays?.split(',')?.map((day) => Number(day)) ||
    locationWorkDays?.split(',')?.map((day) => Number(day)) ||
    workDaysArray ||
    [];
  return Boolean(workDays.includes(dayjs(date).day()));
};

const hasNoLunchTime = (locationObj) => {
  if (!locationObj || typeof locationObj !== 'object') {
    return true;
  }

  return (
    locationObj.lunchStartHours === null ||
    locationObj.lunchStartMinutes === null ||
    locationObj.lunchEndHours === null ||
    locationObj.lunchEndMinutes === null
  );
};

/**
 * Determines if the given day is unavailable or not.
 * @param {Date} date Date to be checked
 * @param {object} location Location object. If `location` is not provided, `locationUnavailableDays` will be used next
 * @param {object[]} locationUnavailableDays Array of unavailable days
 * @returns {boolean} `true` if day is unavailable, `false` otherwise
 * @deprecated Location unavailability should only be checked on the backend.
 */
const dayIsUnavailable = (date, location = null, locationUnavailableDays = []) => {
  if (!(date instanceof Date || date instanceof dayjs)) return false;

  // Only consider all-day unavailable days for marking the whole day as unavailable
  const unavailableDays = (location?.UnavailableDays || locationUnavailableDays || []).filter((ud) => ud.allDay);

  // recurring and non-recurring dates
  const udRecurring = unavailableDays?.filter((ud) => ud.recurring) || [];
  const udNonRecurring = unavailableDays?.filter((ud) => !ud.recurring) || [];

  const unavailableRecurring = udRecurring.find((ud) => {
    const udDate = dayjs(ud.date);
    // check if day of the month and month are the same
    return Boolean(dayjs(date).get('date') === dayjs(udDate).get('date') && dayjs(date).get('month') === dayjs(udDate).get('month'));
  });
  const unavailableNonRecurring = udNonRecurring.find((ud) => {
    const udDateISO = dayjs(ud.date).format('YYYY-MM-DD');
    // check if both ISO dates and time are the same
    return Boolean(udDateISO === dayjs(date).format('YYYY-MM-DD'));
  });

  return Boolean(unavailableRecurring || unavailableNonRecurring);
};

/**
 * Checks if a given date and time slot is in the locations future.
 * Useful to make sure you do not allow bookings in the past.
 *
 * @param {Date|dayjs.Dayjs} date - The date to check
 * @param {Object} timeSlot - Time slot object containing start time
 * @param {Object} location - Location object with timezone information
 * @returns {boolean} Returns true if the booking date is after the current date in the location's timezone, false otherwise
 */
const isTimeSlotInLocationFuture = (date, timeSlot, location) => {
  if (!(date instanceof Date || date instanceof dayjs) || !timeSlot || !location) {
    return false;
  }

  try {
    const timezone = location.timezone || null;
    const dateAtLocationNow = dayjs.tz(dayjs(), timezone);
    const bookingDate = dayjs.tz(`${dayjs(date).format('YYYY-MM-DD')}T${dayjs(timeSlot.start).format('HH:mm')}`, timezone);

    return bookingDate.isAfter(dateAtLocationNow);
  } catch {
    return false;
  }
};

/**
 * Validates if a given date is valid based on the provided recurrence rule.
 *
 * @param {Object} paramObject - An object containing parameters:
 *   @param {dayjs} dayjsdate - The date to be validated, which is in DayJS data type.
 *   @param {dayjs} cycleStartDate - The start date of the cycle.
 *   @param {Array} workdays - An array of weekdays for the rule.
 *   @param {number} weeklyInterval - The interval for weekly recurrence.
 *   @param {RRule} rruleObj - The RRule object for recurrence.
 *   @param {string} rruleStr - The RRule string representation.
 * @returns {boolean} Returns true if the date is valid, according to the rule, false otherwise.
 */
const dateIsValidPerRRULE = ({ dayjsdate, cycleStartDate, workdays, weeklyInterval, rruleObj, rruleStr }) => {
  if (!(dayjsdate instanceof dayjs)) return false;

  let isValid = true;

  let rule;

  if (rruleObj && rruleObj instanceof RRule) {
    rule = rruleObj;
  } else if (rruleStr && typeof rruleStr === 'string') {
    rule = RRule.fromString(rruleStr);
  } else if (weeklyInterval && cycleStartDate && cycleStartDate instanceof dayjs && workdays.length) {
    const { year, month, day } = getRRuleDateTimeComponents(cycleStartDate);

    rule = new RRule({
      freq: RRule.WEEKLY,
      interval: weeklyInterval,
      dtstart: datetime(year, month, day),
      byweekday: workdays
    });
  } else {
    return isValid;
  }

  const startDate = dayjsdate.subtract(1, 'day').toDate();
  const thirtyDaysFromStartDate = dayjsdate.add(30, 'days').toDate();

  // https://github.com/jkbrzt/rrule?tab=readme-ov-file#usage
  // get all occurrence dates within a specific period based on the rrule

  const recurrenceSet = rule.between(startDate, thirtyDaysFromStartDate);

  isValid = recurrenceSet.some((date) => dayjs(date).isSame(dayjsdate, 'date'));

  return isValid;
};

const dateIsValidPerRRulesOfAllGZLocations = (dayjsdate, groundZeroLocations) => {
  let isValid = true;

  if (!groundZeroLocations.length) {
    return isValid;
  }

  const allRRules = groundZeroLocations.map((gzLoc) => gzLoc.rrule).filter((rrule) => rrule !== null);

  const validityCheckResultArray = [];

  for (const rrule of allRRules) {
    const rruleObj = RRule.fromString(rrule);
    const isDateValid = dateIsValidPerRRULE({ dayjsdate, rruleObj });

    validityCheckResultArray.push(isDateValid);
  }

  isValid = validityCheckResultArray.includes(true);

  return isValid;
};

const getValidManagerSchedules = (date, managerSchedules) => {
  if (!managerSchedules?.length) {
    return [];
  }
  return managerSchedules.filter((schedule) => {
    const { effectiveStartDate, effectiveUntilDate } = schedule;

    // https://day.js.org/docs/en/plugin/is-between
    return effectiveUntilDate
      ? dayjs(date).isBetween(dayjs(effectiveStartDate), dayjs(effectiveUntilDate), 'day', '[]')
      : dayjs(date).isSameOrAfter(dayjs(effectiveStartDate));
  });
};

/**
 * @deprecated Manager schedules should only be checked on the backend.
 */
const dateIsValidPerManagerSchedules = ({ dayjsdate, managerSchedules, calendarMode }) => {
  let isValid = true;

  const validManagerSchedules = getValidManagerSchedules(dayjsdate, managerSchedules);

  if (!validManagerSchedules?.length) {
    return isValid;
  }

  const managerSchedulesWithRrule = managerSchedules.filter((rrule) => rrule !== null);

  const validityCheckResultArray = [];

  for (const managerSchedule of managerSchedulesWithRrule) {
    const rrule = managerSchedule.rrule;
    const rruleObj = RRule.fromString(rrule);
    const isValidDate = dateIsValidPerRRULE({ dayjsdate, rruleObj });
    let isDateTimeValid = isValidDate;

    if (calendarMode) {
      const hasLunchTime =
        validHours(managerSchedule.lunchStartHours) &&
        validMinutes(managerSchedule.lunchStartMinutes) &&
        validHours(managerSchedule.lunchEndHours) &&
        validMinutes(managerSchedule.lunchEndMinutes);

      const scheduleTimeRange = {
        start: dayjs(dayjsdate).set('hour', managerSchedule.workStartHours).set('minute', managerSchedule.workStartMinutes),
        end: dayjs(dayjsdate).set('hour', managerSchedule.workEndHours).set('minute', managerSchedule.workEndMinutes)
      };
      const validTime = dayjsdate.isSameOrAfter(scheduleTimeRange.start) && dayjsdate.isBefore(scheduleTimeRange.end);
      isDateTimeValid = isValidDate && validTime;

      if (hasLunchTime) {
        const lunchTimeRange = {
          start: dayjs(dayjsdate).set('hour', managerSchedule.lunchStartHours).set('minute', managerSchedule.lunchStartMinutes),
          end: dayjs(dayjsdate).set('hour', managerSchedule.lunchEndHours).set('minute', managerSchedule.lunchEndMinutes)
        };
        const lunchConflict = dayjsdate.isSameOrAfter(lunchTimeRange.start) && dayjsdate.isBefore(lunchTimeRange.end);
        isDateTimeValid = isValidDate && validTime && !lunchConflict;
      }
    }

    validityCheckResultArray.push(isDateTimeValid);
  }

  isValid = validityCheckResultArray.includes(true);

  return isValid;
};

const dateIsValidAsPerTimezone = (date, timeSlot, timezone = null) => {
  if (!(date instanceof Date || date instanceof dayjs) || !timeSlot) {
    return false;
  }

  try {
    const dateAtTimezoneNow = dayjs.tz(dayjs(), timezone);
    const bookingDate = dayjs.tz(`${dayjs(date).format('YYYY-MM-DD')}T${dayjs(timeSlot.start).format('HH:mm')}`, timezone);

    return bookingDate.isAfter(dateAtTimezoneNow);
  } catch {
    return false;
  }
};

const getSelectedGZLocationPerRRule = (dateValue, gzLocations) =>
  gzLocations.find((gzLoc) => dateIsValidPerRRULE({ dayjsdate: dayjs(dateValue), rruleStr: gzLoc.rrule }));

const getEarliestAvailableDate = (location, managerSchedules = null) => {
  let currentDate = dayjs();

  if (!location) {
    return currentDate;
  }

  for (let index = 0; index <= 60; index += 1) {
    // consider the manager's schedule is one is provided
    let managerSchedulesOkay = true;
    if (managerSchedules) {
      managerSchedulesOkay = dateIsValidPerManagerSchedules({ dayjsdate: currentDate, managerSchedules });
    }

    // if date found that passes all checks return it
    if (dayIsValidWorkday(currentDate, location) && !dayIsUnavailable(currentDate, location) && managerSchedulesOkay) {
      return currentDate;
    }
    currentDate = currentDate.add(1, 'day');
  }

  // if no dates found within 60 days, return null
  return null;
};

const getEarliestDateForGZLocation = (gzLocation) => {
  let currentDate = dayjs();

  if (!gzLocation) {
    return currentDate;
  }

  // search at least 120 days until an available date is found
  for (let index = 0; index <= 120; index += 1) {
    if (dateIsValidPerRRULE({ dayjsdate: currentDate, rruleStr: gzLocation.rrule })) {
      break;
    }
    currentDate = currentDate.add(1, 'day');
  }

  return currentDate;
};

/**
 * Gets the calendar events of a user in Outlook calendar
 * https://learn.microsoft.com/en-us/graph/api/user-list-calendarview?view=graph-rest-1.0&tabs=http#http-request
 * @param {object} options - The options for getting the calendar events.
 * @param {string} options.userPrincipalName - The user principal name to fetch events for.
 * @param {string | null} [options.managerId] - The manager id to fetch events for. Only used if publicMode is true.
 * @param {Date} options.bookingDate - The start of the time range to fetch events for, in ISO8601 format.
 * @param {boolean} [options.publicMode] - Whether to call the public or private Azure function.
 * @returns {Promise<Array<object>>} An array of calendar events.
 */
const getOutlookCalendarEventsOfUser = async ({
  userPrincipalName,
  managerId = null,
  bookingDate,
  location,
  publicMode,
  appDataSourceId
}) => {
  const { workStartHours, workStartMinutes, workEndHours, workEndMinutes, timezone } = location || {};

  const requestForOutlookCalendarEvents = {
    url: publicMode ? `public/outlook-calendar/events` : `outlook-calendar/events`,
    method: 'get',
    params: {
      userPrincipalName,
      managerId: publicMode ? managerId : null,
      timeMin: dayjs
        .tz(
          `${dayjs(bookingDate).format('YYYY-MM-DD')}T${String(workStartHours || 8).padStart(2, '0')}:${String(workStartMinutes || 0).padStart(2, '0')}:00`,
          timezone
        )
        .toISOString(),
      timeMax: dayjs
        .tz(
          `${dayjs(bookingDate).format('YYYY-MM-DD')}T${String(workEndHours || 17).padStart(2, '0')}:${String(workEndMinutes || 0).padStart(2, '0')}:00`,
          timezone
        )
        .toISOString(),
      timezone
    },
    appDataSourceId
  };
  const response = publicMode
    ? await callAzureFunctionPublic(requestForOutlookCalendarEvents)
    : await callAzureFunction(requestForOutlookCalendarEvents);

  const events = response?.data;

  return events || [];
};

/**
 * Gets the calendar events of a user in Google calendar
 * https://developers.google.com/calendar/api/v3/reference/events/list
 * @param {object} options - The options for getting the calendar events.
 * @param {string} options.userEmailAddress - The user principal name to fetch events for.
 * @param {string} [options.managerId] - The manager id to fetch events for. Only used if publicMode is true.
 * @param {Date} options.bookingDate - The start of the time range to fetch events for, in ISO8601 format.
 * @param {object} [options.location] - The location object for which to fetch events. The location object is used to filter the events by the work hours of the location.
 * @param {boolean} [options.publicMode] - Whether to call the public or private Azure function.
 * @returns {Promise<Array<object>>} An array of calendar events.
 */
const getGoogleCalendarEventsOfUser = async ({ userEmailAddress, managerId, bookingDate, location, publicMode, appDataSourceId }) => {
  const { workStartHours, workStartMinutes, workEndHours, workEndMinutes, timezone } = location || {};

  const requestForGoogleCalendarEvents = {
    url: publicMode ? `public/google-calendar/events` : `google-calendar/events`,
    method: 'get',
    params: {
      subject: userEmailAddress,
      managerId: publicMode ? managerId : null,
      timeMin: dayjs
        .tz(
          `${dayjs(bookingDate).format('YYYY-MM-DD')}T${String(workStartHours || 8).padStart(2, '0')}:${String(workStartMinutes || 0).padStart(2, '0')}:00`,
          timezone
        )
        .toISOString(),
      timeMax: dayjs
        .tz(
          `${dayjs(bookingDate).format('YYYY-MM-DD')}T${String(workEndHours || 17).padStart(2, '0')}:${String(workEndMinutes || 0).padStart(2, '0')}:00`,
          timezone
        )
        .toISOString(),
      timeZone: timezone
    },
    appDataSourceId
  };

  const responseForGoogleCalendarEvents = publicMode
    ? await callAzureFunctionPublic(requestForGoogleCalendarEvents)
    : await callAzureFunction(requestForGoogleCalendarEvents);

  const events = responseForGoogleCalendarEvents.data;

  return events || [];
};

const getAllTimeSlotIntervalStartTimes = (workStartTime, workEndTime, timeSlotInterval) => {
  const timeSlotIntervalStartTimes = [];

  if (!timeSlotInterval) {
    return timeSlotIntervalStartTimes;
  }

  let startTime = workStartTime;

  while (dayjs(startTime).isBefore(dayjs(workEndTime))) {
    const newIntervalStartTime = dayjs(startTime).add(timeSlotInterval, 'minute');

    timeSlotIntervalStartTimes.push(startTime);

    startTime = newIntervalStartTime;
  }

  return timeSlotIntervalStartTimes;
};

/**
 * Given an array of start times to check, the latest time slot end and lunch time, determines which next start time to check.
 * If there is a lunch conflict, the next start time to check is the end of the lunch period.
 * If there are more start times to check, the next start time to check is the next start time in the list.
 * Otherwise, the next start time to check is the end of the period.
 *
 * @param {Object} options - An object containing the following properties:
 *   @param {boolean} lunchConflict - Whether there is a lunch conflict.
 *   @param {dayjs} lunchEndTime - Lunch end time.
 *   @param {string} end - The end time of the time slot generated.
 *   @param {dayjs[]} startTimesToCheck - An array of start times to check.
 *   @param {number} indexOfNextStartTimeToCheck - The current index of start times to check.
 * @returns {Object} An object containing:
 *   @property {dayjs} nextStartTime - The next start time to check.
 *   @property {number} nextIndexOfStartTimeToCheck - The updated start time tracker.
 */
const getNextStartTimeToCheck = ({ lunchConflict, lunchEndTime, end, startTimesToCheck, indexOfNextStartTimeToCheck }) => {
  if (lunchConflict) {
    return {
      nextStartTime: lunchEndTime,
      nextIndexOfStartTimeToCheck: indexOfNextStartTimeToCheck
    };
  }

  const hasMoreStartTimesToCheck = startTimesToCheck.length > 0 && indexOfNextStartTimeToCheck < startTimesToCheck.length;

  if (hasMoreStartTimesToCheck) {
    const nextStartTimeToCheck = startTimesToCheck[indexOfNextStartTimeToCheck];

    if (dayjs(nextStartTimeToCheck).isSameOrBefore(end)) {
      return {
        nextStartTime: nextStartTimeToCheck,
        nextIndexOfStartTimeToCheck: indexOfNextStartTimeToCheck + 1
      };
    }
  }

  return {
    nextStartTime: end,
    nextIndexOfStartTimeToCheck: indexOfNextStartTimeToCheck
  };
};

const createDayjsTime = (hours, minutes) => dayjs().set('hour', hours).set('minute', minutes);

// Helper functions for time comparison
const getLatestTime = (current, comparison) => {
  const currentTime = createDayjsTime(current.hours, current.minutes);
  const comparisonTime = createDayjsTime(comparison.hours, comparison.minutes);
  return currentTime.isAfter(comparisonTime) ? current : comparison;
};

const getEarliestTime = (current, comparison) => {
  const currentTime = createDayjsTime(current.hours, current.minutes);
  const comparisonTime = createDayjsTime(comparison.hours, comparison.minutes);
  return currentTime.isBefore(comparisonTime) ? current : comparison;
};

/**
 * Given a location schedule and an array of manager schedules, returns a schedule
 * with the latest work start time and the earliest work end time.
 * If the array of manager schedules is empty, the location schedule is returned.
 * @param {object} locationSchedule - The schedule of the location.
 * @param {Array<object>} managerSchedules - An array of manager schedules.
 * @param {Date} date - The date for which to get the valid time slot schedule.
 * @returns {object} A schedule with the latest work start time and the earliest work end time.
 */
const getLongestWorkdayFromTwoSchedules = (locationSchedule, managerSchedules, date) => {
  if (!managerSchedules.length) {
    return locationSchedule;
  }

  return (
    managerSchedules
      // filter work schedule that is only applicable to the date per rrule
      .filter((schedule) => dateIsValidPerRRULE({ dayjsdate: date, rruleObj: RRule.fromString(schedule.rrule) }))
      .reduce((schedule, current) => {
        // Get the latest work start time
        const latestWorkStart = getLatestTime(
          { hours: current.workStartHours, minutes: current.workStartMinutes },
          { hours: schedule.workStartHours, minutes: schedule.workStartMinutes }
        );

        // Get the earliest work end time
        const earliestWorkEnd = getEarliestTime(
          { hours: current.workEndHours, minutes: current.workEndMinutes },
          { hours: schedule.workEndHours, minutes: schedule.workEndMinutes }
        );

        // Update work times
        return {
          ...schedule,
          workStartHours: latestWorkStart.hours,
          workStartMinutes: latestWorkStart.minutes,
          workEndHours: earliestWorkEnd.hours,
          workEndMinutes: earliestWorkEnd.minutes
        };
      }, locationSchedule)
  );
};

/**
 * Gets the available time slots for a service at a location on a given date.
 * The time slots are calculated by taking into account the work hours, lunch break, and existing bookings at the location.
 * The time slots are also filtered to make sure they do not conflict with the manager's calendar events if the manager is in a specific location.
 * If the manager is an MSP, the time slots are not filtered by the manager's calendar events.
 * @param {object} options - The options for getting the available time slots.
 * @param {Date} options.date - The date to get the available time slots for.
 * @param {object} options.service - The service object for which to get the available time slots.
 * @param {object} options.location - The location object for which to get the available time slots.
 * @param {object} options.manager - The manager object for which to get the available time slots. The manager object is used to filter the time slots by the manager's calendar events.
 * @param {boolean} options.managerHasOutlookCalendar - Whether the manager has an Outlook calendar.
 * @param {boolean} options.managerHasGoogleCalendar - Whether the manager has a Google calendar.
 * @param {boolean} options.publicMode - Whether to call the public or private Azure function.
 * @param {number} options.managerOption - The selected manager location option. Allows calendar event conflict checks if the value is specific manager at location.
 * @param {array} options.managerSchedules - An array of manager schedules.
 * @param {number} options.timeSlotInterval - The time slot interval in minutes to be scanned.
 * @returns {Promise<Array<object>>} An array of available time slots.
 */
const getAvailableTimeSlots = async ({
  date,
  service,
  location,
  manager,
  managerHasOutlookCalendar = false,
  managerHasGoogleCalendar = false,
  publicMode = false,
  managerSchedules = [],
  managerOption,
  timeSlotInterval = 0,
  appDataSourceId
}) => {
  const timeSlots = [];

  if (!service || !location || !date) {
    return timeSlots;
  }

  // The duration of the time slot i.e., the start-to-end time of a slot will always be the service duration.
  // If the service duration is 30 minutes, then time slots by default will be 10:00-10:30, 10:30-11:00, 11:00-11:30, ...
  const timeSlotDuration = service.duration;

  // The frequency of time slots may be altered by the time slot interval, allowing overlapping time slots.
  // e.g., if the time slot interval is 15 minutes, then time slots will be 10:00-10:30, 10:15-10:45, 10:30-11:00, ...
  // Note that the time slots occur more frequently, but still respect the duration of the service
  const timeSlotFrequency = timeSlotInterval || service.duration;

  const validManagerSchedules = getValidManagerSchedules(date, managerSchedules);

  const { workStartHours, workStartMinutes, workEndHours, workEndMinutes } = getLongestWorkdayFromTwoSchedules(
    location,
    validManagerSchedules,
    date
  );

  let workStartTime = null;
  let workEndTime = null;

  if (validHours(workStartHours) && validMinutes(workStartMinutes) && validHours(workEndHours) && validMinutes(workEndMinutes)) {
    workStartTime = getDateWithCustomTime(workStartHours, workStartMinutes, 0, 0, date);
    workEndTime = getDateWithCustomTime(workEndHours, workEndMinutes, 0, 0, date);
  } else {
    return timeSlots;
  }

  // Calculate the current time in location's timezone once at the beginning
  const timezone = location.timezone || null;
  const currentDateTimeAtLocation = dayjs.tz(dayjs(), timezone);

  const locationLunchTimeRange = {
    start: getDateWithCustomTime(location.lunchStartHours, location.lunchStartMinutes, 0, 0, date),
    end: getDateWithCustomTime(location.lunchEndHours, location.lunchEndMinutes, 0, 0, date)
  };

  const managerScheduleLunchTimeRanges = validManagerSchedules.map((schedule) => ({
    start: getDateWithCustomTime(schedule.lunchStartHours, schedule.lunchStartMinutes, 0, 0, date),
    end: getDateWithCustomTime(schedule.lunchEndHours, schedule.lunchEndMinutes, 0, 0, date)
  }));

  const request = {
    url: publicMode ? `/public/bookings/location/${location.id}` : `bookings/location/${location.id}`,
    method: 'get',
    params: {
      date: dayjs(date).format('YYYY-MM-DD'),
      excludeStatuses: [bookingStatuses.Canceled.value, bookingStatuses.DidNotAttend.value].join(',')
    },
    appDataSourceId
  };

  // get all bookings for this date at this location
  const response = publicMode ? await callAzureFunctionPublic(request) : await callAzureFunction(request);

  const unavailableBookingSlots = response.data.map((booking) => {
    const { startHours, startMinutes, endHours, endMinutes } = booking;
    return {
      start: getDateWithCustomTime(startHours, startMinutes, 0, 0, date),
      end: getDateWithCustomTime(endHours, endMinutes, 0, 0, date)
    };
  });

  const outlookCalendarEvents =
    managerHasOutlookCalendar && manager && managerOption === managerLocationOptions.specific.value
      ? await getOutlookCalendarEventsOfUser({
          userPrincipalName: manager.emailAddress,
          managerId: manager.id,
          bookingDate: date,
          location,
          publicMode,
          appDataSourceId
        })
      : [];

  const googleCalendarEvents =
    managerHasGoogleCalendar && manager && managerOption === managerLocationOptions.specific.value
      ? await getGoogleCalendarEventsOfUser({
          userEmailAddress: manager.emailAddress,
          managerId: manager.id,
          bookingDate: date,
          location,
          publicMode,
          appDataSourceId
        })
      : [];

  // get all blocked slots at this Location on the selected day
  const getBlockedSlotsAtThisLocRes = await callAzureFunctionPublic({
    url: `/public/locations/blocked-events`,
    method: 'get',
    params: {
      filteredLocationIds: JSON.stringify([location.id]),
      date: dayjs(date).format('YYYY-MM-DD')
    },
    appDataSourceId
  });

  const blockedSlotsAtLocation = getBlockedSlotsAtThisLocRes.data.map((booking) => {
    const { startHours, startMinutes, endHours, endMinutes, allDay, date } = booking;
    if (allDay) {
      return {
        start: dayjs(date),
        end: dayjs(date).add(1, 'day')
      };
    }
    return {
      start: getDateWithCustomTime(startHours, startMinutes, 0, 0, date),
      end: getDateWithCustomTime(endHours, endMinutes, 0, 0, date)
    };
  });

  let blockedSlotsOfManager = [];

  if (managerOption === managerLocationOptions.specific.value) {
    // get all blocked slots of this Manager on the selected day
    const getBlockedSlotsAtThisManagerRes = await callAzureFunctionPublic({
      url: `/public/managers/blocked-events`,
      method: 'get',
      params: {
        filteredManagerIds: JSON.stringify([manager?.id]),
        date: dayjs(date).format('YYYY-MM-DD')
      },
      appDataSourceId
    });

    blockedSlotsOfManager = getBlockedSlotsAtThisManagerRes.data.map((booking) => {
      const { startHours, startMinutes, endHours, endMinutes, allDay, date } = booking;
      if (allDay) {
        return {
          start: dayjs(date),
          end: dayjs(date).add(1, 'day')
        };
      }
      return {
        start: getDateWithCustomTime(startHours, startMinutes, 0, 0, date),
        end: getDateWithCustomTime(endHours, endMinutes, 0, 0, date)
      };
    });
  }

  // Collect all potentially blocking items
  const allBlockingItems = [
    ...unavailableBookingSlots,
    ...blockedSlotsAtLocation,
    ...blockedSlotsOfManager,
    ...outlookCalendarEvents.map((event) => ({
      start: getDateWithCustomTime(event.startHours, event.startMinutes, 0, 0, event.startDate),
      end: getDateWithCustomTime(event.endHours, event.endMinutes, 0, 0, event.endDate)
    })),
    ...googleCalendarEvents.map((event) => ({
      start: getDateWithCustomTime(event.startHours, event.startMinutes, 0, 0, event.startDate),
      end: getDateWithCustomTime(event.endHours, event.endMinutes, 0, 0, event.endDate)
    }))
  ];

  // Add lunch time ranges if they exist and are valid
  if (
    locationLunchTimeRange.start &&
    locationLunchTimeRange.end &&
    !dayjs(locationLunchTimeRange.start).isSame(locationLunchTimeRange.end)
  ) {
    allBlockingItems.push(locationLunchTimeRange);
  }

  managerScheduleLunchTimeRanges.forEach((range) => {
    if (range.start && range.end && !dayjs(range.start).isSame(range.end)) {
      allBlockingItems.push(range);
    }
  });

  // Optimization: Create an array of busy intervals by merging overlapping intervals
  if (allBlockingItems.length > 0) {
    // Sort intervals by start time
    const sortedIntervals = [...allBlockingItems].sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());

    // Merge overlapping intervals to create a consolidated list of busy periods
    const mergedBusyIntervals = [];
    let currentInterval = sortedIntervals[0];

    for (let i = 1; i < sortedIntervals.length; i += 1) {
      const interval = sortedIntervals[i];

      // If the current interval overlaps with the previous one, merge them
      if (dayjs(interval.start).valueOf() <= dayjs(currentInterval.end).valueOf()) {
        currentInterval = {
          start: currentInterval.start,
          end: dayjs(interval.end).isAfter(currentInterval.end) ? interval.end : currentInterval.end
        };
      } else {
        // No overlap, add the current interval to the result and move to the next one
        mergedBusyIntervals.push(currentInterval);
        currentInterval = interval;
      }
    }

    // Add the last interval
    mergedBusyIntervals.push(currentInterval);

    // Generate available time slots based on the merged busy intervals
    let currentTime = workStartTime;

    // Check if the current time at location is after work start time
    // Only apply this check for the current day, not for future days
    if (currentDateTimeAtLocation.isAfter(dayjs(currentTime)) && dayjs(date).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')) {
      // Adjust currentTime to be the current time at location's timezone
      currentTime = currentDateTimeAtLocation;
    } else {
      // For future days, ensure we start from the work start time
      // and not from midnight (fixes issue when selecting a manager)
      currentTime = workStartTime;
    }

    for (let i = 0; i <= mergedBusyIntervals.length; i += 1) {
      let intervalEnd;

      if (i === mergedBusyIntervals.length) {
        // After the last busy interval, consider the remaining time until work end
        intervalEnd = workEndTime;
      } else {
        // Set the end point at the start of the next busy interval
        intervalEnd = mergedBusyIntervals[i].start;
      }

      // If the current time is before the current busy interval starts
      if (i > 0 && dayjs(currentTime).isBefore(dayjs(mergedBusyIntervals[i - 1].end))) {
        // Move to the end of the previous busy interval
        currentTime = mergedBusyIntervals[i - 1].end;
      }

      // Generate time slots until the start of the next busy interval (or work end time)
      while (
        dayjs(currentTime).isBefore(dayjs(intervalEnd)) &&
        dayjs(currentTime).add(timeSlotDuration, 'minute').isSameOrBefore(dayjs(workEndTime))
      ) {
        const slotEnd = dayjs(currentTime).add(timeSlotDuration, 'minute');

        // Create the potential time slot
        const potentialSlot = {
          start: currentTime,
          end: slotEnd
        };

        // Check if this potential slot overlaps with any of the original blocking items
        // This is a final safety check in case the merging algorithm missed any overlaps
        const hasConflict = allBlockingItems.some((blocker) =>
          areIntervalsOverlapping({ start: potentialSlot.start, end: potentialSlot.end }, { start: blocker.start, end: blocker.end })
        );

        // Only add the slot if it doesn't conflict and is in the future
        if (!hasConflict) {
          // Check if the slot is entirely in the future
          const slotTimeInLocationTZ = dayjs.tz(`${dayjs(date).format('YYYY-MM-DD')}T${dayjs(currentTime).format('HH:mm')}`, timezone);
          if (slotTimeInLocationTZ.isAfter(currentDateTimeAtLocation) || slotTimeInLocationTZ.isSame(currentDateTimeAtLocation)) {
            timeSlots.push({
              start: currentTime,
              startText: dayjs(currentTime).format('hh:mm a'),
              end: slotEnd,
              endText: slotEnd.format('hh:mm a')
            });
          }
        }

        // Move to the next potential slot based on frequency
        currentTime = dayjs(currentTime).add(timeSlotFrequency, 'minute');
      }

      // If we reached a busy interval, set current time to the end of that interval
      if (i < mergedBusyIntervals.length) {
        currentTime = mergedBusyIntervals[i].end;
      }
    }
  } else {
    // No blocking items, generate all possible slots within work hours
    let currentStart = workStartTime;

    while (dayjs(currentStart).isBefore(dayjs(workEndTime))) {
      const currentEnd = dayjs(currentStart).add(timeSlotDuration, 'minute');

      // If the end time exceeds work end time, break
      if (currentEnd > workEndTime) {
        break;
      }

      // Check if current slot is in location's future
      const slotTimeInLocationTZ = dayjs.tz(`${dayjs(date).format('YYYY-MM-DD')}T${dayjs(currentStart).format('HH:mm')}`, timezone);
      if (slotTimeInLocationTZ.isBefore(currentDateTimeAtLocation)) {
        // Move to next slot if current time has already passed
        currentStart = dayjs(currentStart).add(timeSlotFrequency, 'minute');

        continue;
      }

      // Add the time slot
      timeSlots.push({
        start: currentStart,
        startText: dayjs(currentStart).format('hh:mm a'),
        end: currentEnd,
        endText: dayjs(currentEnd).format('hh:mm a')
      });

      // Move to the next slot
      currentStart = dayjs(currentStart).add(timeSlotFrequency, 'minute');
    }
  }

  return timeSlots;
};

const getWorkdaysTextPerRRule = (rruleStr) => {
  let workdaysText = '';

  if (rruleStr && typeof rruleStr === 'string') {
    const rrule = RRule.fromString(rruleStr);

    try {
      const parsedRRule = rrule.toText();
      workdaysText = capitalizeFirstLetter(parsedRRule);
    } catch {
      workdaysText = 'N/A';
    }
  }

  return workdaysText;
};

// TODO: add appDataSourceId
const getMobileServiceProviderTimeSlots = async ({
  date,
  service,
  selectedGroundZeroLocation,
  publicMode = false,
  ignoreGroundZeroLocation = false,
  timezonePerMobileBookingPlaceId,
  timeSlotInterval = 0
}) => {
  const timeSlots = [];

  if (!service || (!ignoreGroundZeroLocation && !selectedGroundZeroLocation) || !date) {
    return timeSlots;
  }

  const {
    workStartHours,
    workStartMinutes,
    workEndHours,
    workEndMinutes,
    lunchStartHours,
    lunchStartMinutes,
    lunchEndHours,
    lunchEndMinutes,
    timezone: groundZeroLocationTimezone
  } = selectedGroundZeroLocation;

  let timezone;

  let workStartTime = null;
  let workEndTime = null;
  let lunchStartTime = null;
  let lunchEndTime = null;

  if (ignoreGroundZeroLocation) {
    // Ignore ground zero location only for unconfirmed bookings; use the mobile booking timezone instead.
    // Default work hours: 8 AM to 5 PM (no lunch break).

    timezone = timezonePerMobileBookingPlaceId;
    workStartTime = getDateWithCustomTime(8, 0, 0, 0, date);
    workEndTime = getDateWithCustomTime(17, 0, 0, 0, date);
  } else {
    timezone = groundZeroLocationTimezone;

    if (validHours(workStartHours) && validMinutes(workStartMinutes) && validHours(workEndHours) && validMinutes(workEndMinutes)) {
      workStartTime = getDateWithCustomTime(workStartHours, workStartMinutes, 0, 0, date);
      workEndTime = getDateWithCustomTime(workEndHours, workEndMinutes, 0, 0, date);
    } else {
      return timeSlots;
    }

    if (validHours(lunchStartHours) && validMinutes(lunchStartMinutes) && validHours(lunchEndHours) && validMinutes(lunchEndMinutes)) {
      lunchStartTime = getDateWithCustomTime(lunchStartHours, lunchStartMinutes, 0, 0, date);
      lunchEndTime = getDateWithCustomTime(lunchEndHours, lunchEndMinutes, 0, 0, date);
    }
  }

  const serviceDuration = service.duration;

  if (!serviceDuration) {
    return timeSlots;
  }

  const requestForBookings = {
    url: publicMode
      ? `public/bookings/manager/${selectedGroundZeroLocation?.managerId}`
      : `bookings/manager/${selectedGroundZeroLocation?.managerId}`,
    method: 'get',
    params: {
      date: dayjs(date).format('YYYY-MM-DD'),
      excludeStatuses: [bookingStatuses.Canceled.value, bookingStatuses.DidNotAttend.value].join(',')
    }
  };

  // get all bookings for this date at this location
  const responseForBookings = publicMode ? await callAzureFunctionPublic(requestForBookings) : await callAzureFunction(requestForBookings);

  const requestForGoogleCalendarEvents = {
    url: publicMode ? `public/google-calendar/events` : `google-calendar/events`,
    method: 'get',
    params: {
      subject: selectedGroundZeroLocation?.emailAddress,
      timeMin: dayjs
        .tz(
          `${dayjs(date).format('YYYY-MM-DD')}T${String(ignoreGroundZeroLocation ? 8 : workStartHours).padStart(2, '0')}:${String(workStartMinutes || 0).padStart(2, '0')}:00`,
          timezone
        )
        .toISOString(),
      timeMax: dayjs
        .tz(
          `${dayjs(date).format('YYYY-MM-DD')}T${String(ignoreGroundZeroLocation ? 17 : workEndHours).padStart(2, '0')}:${String(workEndMinutes || 0).padStart(2, '0')}:00`,
          timezone
        )
        .toISOString(),
      timeZone: timezone
    }
  };

  const responseForGoogleCalendarEvents = publicMode
    ? await callAzureFunctionPublic(requestForGoogleCalendarEvents)
    : await callAzureFunction(requestForGoogleCalendarEvents);

  const mspEmailAddress = selectedGroundZeroLocation?.emailAddress;

  const bookings = responseForBookings.data;
  const googleCalendarEvents = responseForGoogleCalendarEvents.data;
  const outlookCalendarEvents = await getOutlookCalendarEventsOfUser({
    userPrincipalName: mspEmailAddress,
    bookingDate: date,
    managerId: selectedGroundZeroLocation?.managerId,
    timezone,
    publicMode
  });

  const unavailableBookingSlots = bookings?.map((booking) => {
    const { startHours, startMinutes, endHours, endMinutes, isMobileBooking, travelTimeBufferMinutes } = booking;

    let unavailableSlot = {
      start: getDateWithCustomTime(startHours, startMinutes, 0, 0, date),
      end: getDateWithCustomTime(endHours, endMinutes, 0, 0, date)
    };

    if (isMobileBooking) {
      const startDateObj = dayjs(getDateWithCustomTime(startHours, startMinutes, 0, 0, date)).subtract(
        Number(travelTimeBufferMinutes || 0),
        'minute'
      );

      const endDateObj = dayjs(getDateWithCustomTime(endHours, endMinutes, 0, 0, date)).add(Number(travelTimeBufferMinutes || 0), 'minute');

      unavailableSlot = {
        start: startDateObj,
        end: endDateObj
      };
    }

    return unavailableSlot;
  });

  // get all end times of existing bookings
  const allBookingEndTimes = unavailableBookingSlots.map((booking) => booking.end);

  const allGoogleEventEndTimes = googleCalendarEvents.map((event) => {
    const { endDate, endHours, endMinutes } = event;
    return getDateWithCustomTime(endHours, endMinutes, 0, 0, endDate);
  });

  const allOutlookEventEndTimes = outlookCalendarEvents.map((event) => {
    const { endDate, endHours, endMinutes } = event;
    return getDateWithCustomTime(endHours, endMinutes, 0, 0, endDate);
  });

  const allTimeSlotIntervalStartTimes = getAllTimeSlotIntervalStartTimes(workStartTime, workEndTime, timeSlotInterval);

  const startTimesToCheck = [
    ...new Set([...allTimeSlotIntervalStartTimes, ...allBookingEndTimes, ...allGoogleEventEndTimes, ...allOutlookEventEndTimes])
  ] // only check start times that are within the working hours
    .filter((time) => time.isSameOrAfter(workStartTime) && time.isSameOrBefore(workEndTime));

  // start and end time for time slot
  let start = workStartTime;
  let end = dayjs(start).add(serviceDuration, 'minute');

  let indexOfNextStartTimeToCheck = 0;
  startTimesToCheck.sort((a, b) => (dayjs(a).isBefore(dayjs(b)) ? -1 : 1));

  while (start >= workStartTime && start < workEndTime && end > workStartTime && end <= workEndTime) {
    // check for conflicts with lunch time
    const lunchConflict =
      lunchStartTime && lunchEndTime && areIntervalsOverlapping({ start: lunchStartTime, end: lunchEndTime }, { start, end });
    // check for conflicts with at least one existing booking
    const bookingConflict = unavailableBookingSlots.find((slot) =>
      areIntervalsOverlapping({ start: slot.start, end: slot.end }, { start, end })
    );
    // check for conflicts with Google calendar events
    const googleCalendarConflict = googleCalendarEvents?.find((event) => {
      const { startDate, startHours, startMinutes, endDate, endHours, endMinutes } = event;
      const eventStart = getDateWithCustomTime(startHours, startMinutes, 0, 0, startDate);
      const eventEnd = getDateWithCustomTime(endHours, endMinutes, 0, 0, endDate);
      return areIntervalsOverlapping({ start: eventStart, end: eventEnd }, { start, end });
    });

    const outlookCalendarConflict = outlookCalendarEvents?.find((event) => {
      const { startDate, startHours, startMinutes, endDate, endHours, endMinutes } = event;
      const eventStart = getDateWithCustomTime(startHours, startMinutes, 0, 0, startDate);
      const eventEnd = getDateWithCustomTime(endHours, endMinutes, 0, 0, endDate);
      return areIntervalsOverlapping({ start: eventStart, end: eventEnd }, { start, end });
    });

    const timeSlot = {
      start,
      startText: dayjs(start).format('hh:mm a'),
      end,
      endText: dayjs(end).format('hh:mm a')
    };

    if (
      !lunchConflict &&
      !bookingConflict &&
      !googleCalendarConflict &&
      !outlookCalendarConflict &&
      !timeSlots.some((ts) => dayjs(ts.start).isSame(dayjs(timeSlot.start))) &&
      (ignoreGroundZeroLocation
        ? dateIsValidAsPerTimezone(date, timeSlot, timezone)
        : isTimeSlotInLocationFuture(date, timeSlot, selectedGroundZeroLocation))
    ) {
      timeSlots.push(timeSlot);
    }

    // get the next start time to check
    const { nextStartTime, nextIndexOfStartTimeToCheck } = getNextStartTimeToCheck({
      lunchConflict,
      lunchEndTime,
      end,
      startTimesToCheck,
      indexOfNextStartTimeToCheck
    });

    start = nextStartTime;
    end = dayjs(start).add(serviceDuration, 'minute');

    indexOfNextStartTimeToCheck = nextIndexOfStartTimeToCheck;
  }

  // sort the time slots
  timeSlots.sort((a, b) => (dayjs(a.start).isBefore(dayjs(b.start)) ? -1 : 1));

  return timeSlots;
};

/**
 * Calculates the time slot interval for a booking page at a given location.
 * @param {string} bookingPageId - Booking page ID
 * @param {object} location - Location object
 * @param {number | null} defaultTimeSlotInterval - Default time slot interval per app config
 * @param {string} appDataSourceId - App data source ID
 * @returns {Promise<number>} Time slot interval in minutes
 */

const getTimeSlotInterval = async (bookingPageId, location, defaultTimeSlotInterval = null, appDataSourceId) => {
  if (!location) {
    return 0;
  }

  const getShortestServiceDuration = async () => {
    let timeSlotInterval = 0;

    const res = await callAzureFunctionPublic({
      url: `public/locations/${location.id}/shortest-service-duration`,
      method: 'get',
      params: { bookingPageId },
      appDataSourceId
    });
    const { shortestServiceDuration } = res.data || {};

    if (typeof shortestServiceDuration === 'number') {
      timeSlotInterval = shortestServiceDuration;
    }

    return timeSlotInterval;
  };

  // Use location's interval if valid and not 0
  if (location.timeSlotInterval != null && location.timeSlotInterval !== 0) {
    return location.timeSlotInterval;
  }

  // Handle specific defaultTimeSlotInterval cases
  if (defaultTimeSlotInterval === '0' || location.timeSlotInterval === 0) {
    // If the default is '0', return the shortest service duration
    return getShortestServiceDuration();
  }

  // Fallback to defaultTimeSlotInterval
  return defaultTimeSlotInterval ?? 0;
};

/**
 * Gets a booking creation origin, i.e., where a booking was created from.
 * @param {*} booking Booking object
 * @returns creation origin string
 */
const getCreatedFrom = (booking = {}) => {
  const { adminCreated, managerCreated, bookingPageId, respondentAvailabilityIsKnown, isMobileBooking } = booking;
  const createdByAdminOrManager = Boolean(adminCreated || managerCreated);
  const fromBookingPage = Boolean(bookingPageId);

  // booking created from a booking page
  if (createdByAdminOrManager && fromBookingPage) {
    return 'Booking Page';
  }

  // booking created from a public booking page
  if (!createdByAdminOrManager && fromBookingPage && isMobileBooking && !respondentAvailabilityIsKnown) {
    return 'Public Booking Page - Get Help From Admin';
  }

  // booking created from a public booking page
  if (!createdByAdminOrManager && fromBookingPage) {
    return 'Public Booking Page';
  }
  // booking created from calendar
  if (createdByAdminOrManager && !fromBookingPage) {
    return 'Calendar';
  }

  return '';
};

const getPreferredTimeFrameLabel = (preferredTimeFrame) => {
  if (!preferredTimeFrame) {
    return '-';
  }

  const preferredTimeFrameOption = preferredTimeFrameOptions.find((option) => option.value === preferredTimeFrame);

  return preferredTimeFrameOption ? preferredTimeFrameOption.label : '-';
};

const isDateRangeOverlap = (range1, range2) => {
  // Destructure start and end dates from both ranges
  const { start: start1, end: end1 } = range1;
  const { start: start2, end: end2 } = range2;

  // Check if the ranges overlap
  return dayjs(start1).isBefore(end2) && dayjs(start2).isBefore(end1);
};

/**
 * Check whether selected slot overlaps to any blocked slot of selected Location
 * @param {object[]} locationBlockedSlots Array of blocked slots from every selected Location
 * @param {object} slotInfo Selected slot details
 * @param {string} mode Calendar mode
 * @param {object} selectedLocationOnLocationAndManagerMode Selected Location on ByLocationAndManager mode
 * @returns {boolean} return `true` if selected slot overlaps to any blocked slot of Locations. otherwise, false
 */
const locationBlockSlotCollision = (locationBlockedSlots, slotInfo, mode, selectedLocationOnLocationAndManagerMode) => {
  let locationIdToMatch = null;

  if (mode === calendarModes.ByLocation || mode === calendarModes.ByTemporaryLocation) {
    locationIdToMatch = slotInfo.resourceId;
  } else if (mode === calendarModes.ByLocationAndManager) {
    locationIdToMatch = selectedLocationOnLocationAndManagerMode.id;
  }

  const isSlotBlocked = locationBlockedSlots.some((item) => {
    const range1 = {
      start: slotInfo.start,
      end: slotInfo.end
    };
    const range2 = {
      start: dayjs(item.date).set('hour', item.startHours).set('minute', item.startMinutes),
      end: dayjs(item.date).set('hour', item.endHours).set('minute', item.endMinutes)
    };

    const isSelectedSlotOverlapsToBlockedSlot = Boolean(
      isDateRangeOverlap(range1, range2) && item.isBlockSlot && item.locationId === locationIdToMatch
    );

    const isSelectedSlotOverlapsToAllDayBlockedSlot = Boolean(
      item.isBlockSlot &&
      item.allDay &&
      dayjs(item.date).format('YYYY-MM-DD') === dayjs(slotInfo.start).format('YYYY-MM-DD') &&
      item.locationId === locationIdToMatch
    );

    return isSelectedSlotOverlapsToBlockedSlot || isSelectedSlotOverlapsToAllDayBlockedSlot;
  });
  return isSlotBlocked || false;
};

/**
 * Check whether selected slot overlaps to any blocked slot of selected Manager
 * @param {object[]} managerBlockedSlots Array of blocked slots from every selected Manager
 * @param {object} slotInfo Selected slot details
 * @returns {boolean} return `true` if selected slot overlaps to any blocked slot of selected Manager. otherwise, false
 */
const managerBlockSlotCollision = (managerBlockedSlots, slotInfo) => {
  const selectedManagerId = slotInfo.resourceId;

  const isSlotBlocked = managerBlockedSlots.some((item) => {
    const range1 = {
      start: slotInfo.start,
      end: slotInfo.end
    };
    const range2 = {
      start: dayjs(item.date).set('hour', item.startHours).set('minute', item.startMinutes),
      end: dayjs(item.date).set('hour', item.endHours).set('minute', item.endMinutes)
    };

    const isSelectedSlotOverlapsToBlockedSlot = Boolean(
      isDateRangeOverlap(range1, range2) && item.isBlockSlot && item.managerId === selectedManagerId
    );

    const isSelectedSlotOverlapsToAllDayBlockedSlot = Boolean(
      item.isBlockSlot &&
      item.allDay &&
      dayjs(item.date).format('YYYY-MM-DD') === dayjs(slotInfo.start).format('YYYY-MM-DD') &&
      item.managerId === selectedManagerId
    );

    return isSelectedSlotOverlapsToBlockedSlot || isSelectedSlotOverlapsToAllDayBlockedSlot;
  });
  return isSlotBlocked || false;
};

const defaultWorkTimeSchedules = {
  workStartTime: getDateWithCustomTime(8, 0, 0, 0),
  workEndTime: getDateWithCustomTime(17, 0, 0, 0),
  lunchStartTime: getDateWithCustomTime(12, 0, 0, 0),
  lunchEndTime: getDateWithCustomTime(13, 0, 0, 0)
};

/**
 * Check if current values are different from cached values
 * @param {object[]} current - current values
 * @param {object[]} cached - cached values
 * @returns {boolean} true if current values are different from cached values, otherwise false
 */

const haveValuesChanged = (current, cached) => {
  let answer = false;

  if (current.length !== cached.length) answer = true;

  if (!current.every((option) => cached.map((each) => each.id).includes(option.id))) return true;

  return answer;
};

const isTimeSlotValid = (booking, timeSlots) =>
  timeSlots?.some(
    (slot) =>
      new Date(slot.start).getTime() === new Date(booking.timeSlot?.start).getTime() &&
      new Date(slot.end).getTime() === new Date(booking.timeSlot?.end).getTime()
  );

const getBookingStatus = (status, affiliateSelected) => {
  let bookingStatus;
  if (affiliateSelected) {
    bookingStatus = Object.values(affiliateBookingStatuses).find((bs) => bs.value === status);
  }
  if (!affiliateSelected || !bookingStatus) {
    bookingStatus = Object.values(bookingStatuses).find((bs) => bs.value === status);
  }
  return bookingStatus;
};

const filterManagerSchedulesByLocation = (managerSchedules, locationId) =>
  managerSchedules?.filter((schedule) =>
    // filter schedules that are applicable to location
    schedule.LocationSchedules?.some((loc) => loc.locationId === locationId)
  ) || [];

export {
  getDateWithCustomTime,
  validHours,
  validMinutes,
  dayIsValidWorkday,
  dayIsUnavailable,
  isTimeSlotInLocationFuture,
  dateIsValidPerManagerSchedules,
  getEarliestAvailableDate,
  getAvailableTimeSlots,
  getCreatedFrom,
  getMobileServiceProviderTimeSlots,
  getSelectedGZLocationPerRRule,
  getWorkdaysTextPerRRule,
  dateIsValidPerRRULE,
  dateIsValidPerRRulesOfAllGZLocations,
  defaultWorkTimeSchedules,
  hasNoLunchTime,
  getPreferredTimeFrameLabel,
  getEarliestDateForGZLocation,
  haveValuesChanged,
  getTimeSlotInterval,
  isDateRangeOverlap,
  locationBlockSlotCollision,
  managerBlockSlotCollision,
  createDayjsTime,
  isTimeSlotValid,
  getBookingStatus,
  filterManagerSchedulesByLocation
};
